import React, { useState, useEffect } from 'react';
import { fetchCompanies as fetchCompaniesAPI, createCompanyWithREST } from '../../api/packageService';
import AddCompanyModal from './AddCompanyModal';

interface Company {
  _id: string;
  companyName?: string;
  name?: string;
  phone?: string | null;
  address?: string;
  description?: string;
  parentCompanyId?: string | null;
  createdBy?: string;
  updatedBy?: string;
  isDeleted?: boolean;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

const CompaniesList: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Modal state
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);

  // const navigate = useNavigate();
  // const location = useLocation();

  useEffect(() => {
    const fetchCompaniesData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('AUTH_TOKEN');
        if (!token) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        const response = await fetchCompaniesAPI();
        
        if (response.companies) {
          // Check if response.companies is a single object or an array
          if (Array.isArray(response.companies)) {
            setCompanies(response.companies);
          } else if (typeof response.companies === 'object' && response.companies !== null) {
            // If it's a single company object, wrap it in an array
            setCompanies([response.companies]);
          } else {
            setCompanies([]);
          }
        } else {
          setError('Failed to fetch companies');
        }
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError('Error fetching companies: ' + (err instanceof Error ? err.message : 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchCompaniesData();
  }, []);

  // Filter companies based on search term
  const filteredCompanies = companies.filter(company =>
    (company.companyName && company.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (company.name && company.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (company.address && company.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (company.phone && company.phone && company.phone.includes(searchTerm))
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCompanies = filteredCompanies.slice(startIndex, startIndex + itemsPerPage);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // const handleAddCompany = () => {
  //   setShowAddCompanyModal(true);
  // };

  return (
    <>
      <div className="flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
        <div className="card border-0 shadow-sm rounded-3 overflow-hidden flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
          <div className="card-header bg-white border-0 py-2">
            <div className="d-flex justify-content-between align-items-center flex-column flex-md-row text-start w-100">
              <div className="text-start mb-2 mb-md-0 w-100" style={{ textAlign: 'left' }}>
                <h2 className="h5 fw-bold text-dark mb-0">Companies Management</h2>
              </div>
              <div className="d-flex flex-row gap-2 w-100 w-md-auto flex-wrap justify-content-end">
                <div className="input-group" style={{ maxWidth: '300px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                </div>
                <button
                  className="btn btn-primary rounded-pill px-2 px-md-2 py-1 py-md-2 flex-shrink-0"
                  onClick={() => setShowAddCompanyModal(true)}
                  style={{
                    fontSize: '0.875rem',
                    color: '#ffffff',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    border: 'none'
                  }}
                >
                  <i className="bi bi-plus-lg me-1" style={{ color: '#ffffff' }}></i>
                  <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Add Company</span>
                  <span className="d-sm-none" style={{ color: '#ffffff' }}>Add</span>
                </button>
              </div>
            </div>
          </div>
          <div className="card-body p-0 flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="table-container rounded-3 shadow-sm flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
              {loading ? (
                <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading companies...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-4 flex-grow-1 d-flex flex-column justify-content-center">
                  {error}
                </div>
              ) : (
                <>
                  <div className="table-wrapper-mobile flex-grow-1" style={{ overflow: 'auto', minHeight: 0 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-primary text-dark sticky-top" style={{ zIndex: 10 }}>
                        <tr>
                          <th className="py-2 px-2 px-md-2 text-start">#</th>
                          <th className="py-2 px-2 px-md-2 text-start">Company Name</th>
                          <th className="py-2 px-2 px-md-2 text-start">Address</th>
                          <th className="py-2 px-2 px-md-2 d-none d-md-table-cell text-start">Phone</th>
                          <th className="py-2 px-2 px-md-2 text-start">Created At</th>
                          <th className="py-2 px-2 px-md-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCompanies.map((company, index) => {
                          // Ensure company is a valid object with required properties
                          if (!company || typeof company !== 'object' || !company._id) {
                            return null;
                          }
                          
                          return (
                            <tr key={company._id}>
                              <td className="py-2 px-2 px-md-2 text-start">
                                <span className="fw-medium">{startIndex + index + 1}</span>
                              </td>
                              <td className="py-2 px-2 px-md-2 text-start">
                                <span className="fw-medium">{company.companyName || company.name || 'N/A'}</span>
                              </td>
                              <td className="py-2 px-2 px-md-2 text-start">
                                {company.address || company.description || 'N/A'}
                              </td>
                              <td className="py-2 px-2 px-md-2 d-none d-md-table-cell text-start">
                                {company.phone || 'N/A'}
                              </td>
                              <td className="py-2 px-2 px-md-2 text-start">
                                {(company.createdAt || company.created_at) ? new Date(company.createdAt || company.created_at as string).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="text-center py-2 px-2 px-md-2">
                                <div className="d-flex justify-content-center gap-1 gap-md-2">
                                  <button 
                                    className="btn btn-outline-primary btn-sm rounded-pill px-2 px-md-3" 
                                    title="Edit Company"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button 
                                    className="btn btn-outline-danger btn-sm rounded-pill px-2 px-md-3" 
                                    title="Delete Company"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }).filter(Boolean)} 
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center p-2 p-md-2 table-pagination-mobile" style={{ flexShrink: 0 }}>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="me-2 small">Items per page:</span>
                      <select
                        className="form-select form-select-sm"
                        value={itemsPerPage}
                        disabled
                        style={{ width: '80px' }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="ms-2 ms-md-3 small">Total: {filteredCompanies.length} companies</span>
                    </div>
                    
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                            <span className="d-none d-sm-inline">Previous</span>
                            <span className="d-sm-none">Prev</span>
                          </button>
                        </li>
                        
                        {/* Generate page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}
                        
                        {totalPages > 5 && currentPage < totalPages - 2 && (
                          <>
                            <li className="page-item disabled"><span className="page-link">...</span></li>
                            <li className="page-item">
                              <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                                {totalPages}
                              </button>
                            </li>
                          </>
                        )}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                            <span className="d-none d-sm-inline">Next</span>
                            <span className="d-sm-none">Next</span>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {showAddCompanyModal && (
        <AddCompanyModal 
          isOpen={showAddCompanyModal} 
          onClose={() => setShowAddCompanyModal(false)} 
          onSubmit={async (companyData) => {
            try {
              await createCompanyWithREST(companyData);
              // Refresh the companies list after successful creation
              const token = localStorage.getItem('AUTH_TOKEN');
              if (!token) {
                setError('User not authenticated');
                return;
              }
              
              const response = await fetchCompaniesAPI();
              
              if (response.companies) {
                // Check if response.companies is a single object or an array
                if (Array.isArray(response.companies)) {
                  setCompanies(response.companies);
                } else if (typeof response.companies === 'object' && response.companies !== null) {
                  // If it's a single company object, wrap it in an array
                  setCompanies([response.companies]);
                } else {
                  setCompanies([]);
                }
              }
              
              setShowAddCompanyModal(false);
            } catch (err) {
              console.error('Error creating company:', err);
              setError('Error creating company: ' + (err instanceof Error ? err.message : 'Unknown error'));
            }
          }}
          formLoading={false}
          formError={error}
          formSuccess={null}
        />
      )}
    </>
  );
};

export default CompaniesList;